export const DOSSIER_COMPONENTS = Object.freeze([
  {
    key: 'starterStrength',
    label: 'Starter strength',
    caption: 'Your best legal lineup, measured against the other nine.',
  },
  {
    key: 'totalVorp',
    label: 'Total VORP',
    caption: 'Projected value above replacement, collected across the whole roster.',
  },
  {
    key: 'draftedEfficiency',
    label: 'Drafted efficiency',
    caption: 'What the non-keeper selections returned against their position-specific slot curve.',
  },
  {
    key: 'keeperValue',
    label: 'Keeper value',
    caption: 'The separately priced surplus carried in from last season, outside the draft curve.',
  },
  {
    key: 'benchQuality',
    label: 'Bench quality',
    caption: 'Useful reserve value after the starters have taken their chairs.',
  },
  {
    key: 'byeResilience',
    label: 'Bye resilience',
    caption: 'How much of the starting lineup disappears on the same Sunday.',
  },
  {
    key: 'positionalConstruction',
    label: 'Positional construction',
    caption: 'Whether the roster can legally report for work without obvious waste.',
  },
]);

export function buildContributions(team, weights) {
  return DOSSIER_COMPONENTS.map((component) => {
    const score = team.componentScores[component.key];
    const weight = weights[component.key];
    return {
      ...component,
      score,
      weight,
      contribution: score * weight,
    };
  });
}

export function contributionTotal(team, weights) {
  return buildContributions(team, weights)
    .reduce((total, component) => total + component.contribution, 0);
}

// draftGrades.json carries the filed letters, but not a second, invented cutoff table.
// These bands place boundaries halfway between adjacent filed letter groups so the line
// stays entirely derived from the Registrar's source data.
export function buildFiledGradeBands(teams) {
  const groups = [];
  [...teams]
    .sort((first, second) => second.score - first.score)
    .forEach((team) => {
      const current = groups[groups.length - 1];
      if (current?.letter === team.letter) current.scores.push(team.score);
      else groups.push({ letter: team.letter, scores: [team.score] });
    });

  return groups.map((group, index) => {
    const next = groups[index + 1];
    const previous = groups[index - 1];
    const high = previous
      ? (Math.min(...previous.scores) + Math.max(...group.scores)) / 2
      : 100;
    const low = next
      ? (Math.min(...group.scores) + Math.max(...next.scores)) / 2
      : 0;
    return { letter: group.letter, low, high };
  });
}

const SMALL_NUMBERS = [
  'zero', 'one', 'two', 'three', 'four', 'five', 'six', 'seven', 'eight', 'nine',
  'ten', 'eleven', 'twelve', 'thirteen', 'fourteen', 'fifteen', 'sixteen', 'seventeen',
  'eighteen', 'nineteen',
];
const TENS = ['', '', 'twenty', 'thirty', 'forty', 'fifty', 'sixty', 'seventy', 'eighty', 'ninety'];

export function numberToWords(value) {
  const number = Math.trunc(Number(value));
  if (Number.isNaN(number)) return '';
  if (number < 0) return String(number);
  if (number < 20) return SMALL_NUMBERS[number];
  if (number < 100) {
    const tens = TENS[Math.floor(number / 10)];
    const ones = number % 10 ? `-${SMALL_NUMBERS[number % 10]}` : '';
    return `${tens}${ones}`;
  }
  if (number < 1000) {
    const hundreds = SMALL_NUMBERS[Math.floor(number / 100)];
    const remainder = number % 100 ? ` and ${numberToWords(number % 100)}` : '';
    return `${hundreds} hundred${remainder}`;
  }
  return String(number);
}

export function formatCountWords(value, { capitalize = false } = {}) {
  const words = numberToWords(value);
  if (!words) return '';
  return capitalize ? words.charAt(0).toUpperCase() + words.slice(1) : words;
}

export function formatMeasurementDek(count) {
  const countWord = formatCountWords(count, { capitalize: true });
  const noun = count === 1 ? 'measurement' : 'measurements';
  return `${countWord} ${noun}. One hundred available points. No participation credit.`;
}
